import { useMemo, useState } from "react";
import { Search, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const PAIRABLE = new Set(["arrived", "available", "resting", "on_court", "assigned"]);
const MAX_PARTNERS = 3;

export const groupedPlayerIds = (playRequests) => {
  const s = new Set();
  for (const r of playRequests || []) {
    if (!["pending", "locked"].includes(r.status)) continue;
    for (const m of r.members || []) if (m.response !== "rejected") s.add(m.player_id);
  }
  return s;
};

export const canPairPlayer = (p, grouped) =>
  !!p && PAIRABLE.has(p.status) && !p.done_for_now && !grouped.has(p.id);

export function AssignPartnerModal({ festivalId, player, players, playRequests, onClose, onDone }) {
  const [picked, setPicked] = useState([]);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const grouped = useMemo(() => groupedPlayerIds(playRequests), [playRequests]);
  const candidates = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (players || [])
      .filter((p) => p.id !== player.id && canPairPlayer(p, grouped))
      .filter((p) => !needle || `${p.first_name || ""} ${p.last_name || ""}`.toLowerCase().includes(needle))
      .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`));
  }, [players, player.id, grouped, q]);

  const toggle = (pid) => setPicked((cur) => (
    cur.includes(pid) ? cur.filter((x) => x !== pid) : cur.length >= MAX_PARTNERS ? cur : [...cur, pid]
  ));

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/festival/${festivalId}/play-requests/staff-assign`, {
        player_id: player.id, partner_ids: picked,
      });
      toast.success(`${player.first_name} paired — both players notified`);
      onDone?.();
      onClose();
    } catch (e) {
      if (!e?.__helperKickout) toast.error(e?.response?.data?.detail || "Couldn't assign partner");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[96] bg-black/80 flex items-end sm:items-center justify-center p-3" onClick={onClose} data-testid="assign-partner-modal">
      <div className="w-full max-w-md bg-[#0A0A0A] border-2 border-[#CCFF00]" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center justify-between gap-2 px-4 py-3 border-b border-[#262626]">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[#CCFF00]">Assign partner · All-In</div>
            <div className="text-sm text-white truncate mt-0.5" data-testid="assign-partner-player-name">{player.first_name} {player.last_name}</div>
          </div>
          <button type="button" onClick={onClose} data-testid="assign-partner-close" className="p-1 border border-[#262626] text-[#E0E0E0] hover:text-white hover:border-white" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </header>
        <div className="px-4 pt-3">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-[#B0B0B0]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search players" data-testid="assign-partner-search" className="w-full bg-[#141414] border border-[#262626] pl-7 pr-2 py-2 text-sm text-white focus:border-[#CCFF00] outline-none" />
          </div>
          <div className="mt-2 text-[11px] text-[#B0B0B0]">Pick up to {MAX_PARTNERS} partners. The group is confirmed immediately and seated together.</div>
        </div>
        <ul className="mt-2 max-h-[50vh] overflow-y-auto divide-y divide-[#1F1F1F]">
          {candidates.length === 0 && (
            <li className="px-4 py-8 text-center text-sm text-[#B0B0B0]" data-testid="assign-partner-empty">No available players to pair.</li>
          )}
          {candidates.map((p) => {
            const on = picked.includes(p.id);
            const full = !on && picked.length >= MAX_PARTNERS;
            return (
              <li key={p.id}>
                <button type="button" disabled={full} onClick={() => toggle(p.id)} data-testid={`assign-partner-option-${p.id}`} className={`w-full text-left px-4 py-2.5 flex items-center justify-between gap-2 transition-colors hover:bg-[#141414] disabled:opacity-40 ${on ? "bg-[#1A2200]" : ""}`}>
                  <span className="text-sm text-white truncate">{p.first_name} {p.last_name}</span>
                  <span className={`text-[10px] uppercase tracking-widest ${on ? "text-[#CCFF00]" : "text-[#B0B0B0]"}`}>
                    {on ? "Selected" : (p.status || "").replace("_", " ")}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
        <div className="p-4 border-t border-[#262626]">
          <button type="button" disabled={saving || picked.length === 0} onClick={save} data-testid="assign-partner-confirm" className="w-full py-2.5 text-[11px] uppercase tracking-widest font-bold bg-[#CCFF00] text-black hover:bg-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            {saving ? "Pairing…" : `Pair ${picked.length || ""} ${picked.length === 1 ? "partner" : "partners"}`.replace("  ", " ")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AssignPartnerModal;
